import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button, Throbber, CurrencyFormat } from '@lt/components';
import { useCurrency, useTranslation } from '@lt/localization';

import BubbleIncludedServices from 'src/components/Bubbles/BubbleIncludedServices';

import WhatIncludedInPrice from 'src/components/WhatIncludedInPrice';

import {
  pluralizeNights,
  pluralizeTouristsAcc,
} from '../../../../utils/pluralizeUtils';

const SearcherFixedButton = ({
  price,
  onClick,
  nightsCount,
  touristsCount,
  fetching,
  isHotelOnly,
}) => {
  const { code } = useCurrency();
  const { t } = useTranslation();
  const buttonSubtext = `${t('hotel.fixedButton.subtext', {
    count: `${nightsCount}`,
    nights: `${pluralizeNights(nightsCount)}`,
    countTourist: `${touristsCount}`,
    tourists: `${pluralizeTouristsAcc(touristsCount)}`,
  })} ${isHotelOnly ? `` : t('hotel.fixedButton.withFlight')}`;
  const hasPrice = Boolean(price);
  const showTooltip = fetching || hasPrice;
  return (
    <StyledFixedContainer>
      <StyledContainer>
        {!isHotelOnly && (
          <StyledHintContainer>
            {showTooltip ? (
              <BubbleIncludedServices placement="top" eventType="click">
                <WhatIncludedInPrice />
              </BubbleIncludedServices>
            ) : (
              <StyledEmptyToursText>
                {t('hotel.fixedButton.emptyTours')}
              </StyledEmptyToursText>
            )}
          </StyledHintContainer>
        )}
        <StyledButton onClick={onClick} primary size="large">
          {fetching ? (
            <Throbber size={20} color="#f5a623" />
          ) : (
            <>
              {hasPrice ? (
                <>
                  {t('hotel.fixedButton.getPrice', {
                    context: isHotelOnly ? 'hotel' : 'package',
                  })}
                  <CurrencyFormat value={price} currencyCode={code} />
                  <StyledButtonText>{buttonSubtext}</StyledButtonText>
                </>
              ) : (
                t('hotel.fixedButton.changeDate', {
                  context: isHotelOnly ? 'hotel' : 'package',
                })
              )}
            </>
          )}
        </StyledButton>
      </StyledContainer>
    </StyledFixedContainer>
  );
};

const StyledFixedContainer = styled.div`
  position: fixed;
  z-index: 100;
  bottom: 0;
  left: 0;
  width: 100%;
`;

const StyledContainer = styled.div`
  padding: 8px 10px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${(props) => props.theme.colors.beige};
`;

const StyledHintContainer = styled.div`
  margin-bottom: 5px;
  display: flex;
  font-size: 12px;
  color: ${(props) => props.theme.colors.greyLight};
`;

const StyledButton = styled(Button)`
  position: relative;
  width: 100%;
  font-size: 18px;
`;

const StyledButtonText = styled.div`
  font-size: 11px;
  font-weight: normal;
  color: ${(props) => props.theme.colors.blackLight};
`;

const StyledEmptyToursText = styled.span`
  color: ${(props) => props.theme.colors.blackLight};
`;

SearcherFixedButton.propTypes = {
  price: PropTypes.number,
  nightsCount: PropTypes.number.isRequired,
  touristsCount: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  fetching: PropTypes.bool,
};

SearcherFixedButton.defaultProps = {
  fetching: false,
  price: null,
};

export default SearcherFixedButton;
